import type {
  CalendarBoardExternalBusyInterval,
  CalendarBoardPropertyRef,
  CalendarBoardWorkOrderRef,
  CalendarRouteTransition,
} from "../../../../../../../../../api/kehrblatt/schedule-events"
import type { CalendarPlanningSettingsView } from "./planningSettings"
import { normalizeCalendarPlanningSettings } from "./planningSettings"
import { buildContactOrderByEventId, dedupeById } from "./entities"

export type CalendarWindowBounds = {
  startIso: string
  endIso: string
}

export type CalendarBoardProjectionResponse = {
  events?: ScheduleEvent[] | null
  external_busy_intervals?: CalendarBoardExternalBusyInterval[] | null
  route_transitions?: CalendarRouteTransition[] | null
  work_orders?: CalendarBoardWorkOrderRef[] | null
  properties?: CalendarBoardPropertyRef[] | null
  customers?: Customer[] | null
  contact_points?: ContactPoint[] | null
  contact_orders?: ContactOrder[] | null
  planning_settings?: unknown
}

export type CalendarWindowProjection = {
  events: ScheduleEvent[]
  externalBusyIntervals: CalendarBoardExternalBusyInterval[]
  routeTransitions: CalendarRouteTransition[]
  workOrders: CalendarBoardWorkOrderRef[]
  properties: CalendarBoardPropertyRef[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  contactOrderByEventId: Record<string, ContactOrder>
  planningSettings: CalendarPlanningSettingsView
}

export const buildCalendarWindowBounds = ({
  rangeStart,
  rangeExclusiveEnd,
}: {
  rangeStart: Date
  rangeExclusiveEnd: Date
}): CalendarWindowBounds => ({
  startIso: rangeStart.toISOString(),
  endIso: rangeExclusiveEnd.toISOString(),
})

export const buildCalendarWindowKey = ({
  rangeKey,
  employeeIds,
}: {
  rangeKey: string
  employeeIds: string[]
}) => `${rangeKey}|${[...employeeIds].sort().join(",")}`

export const projectCalendarBoardResponse = (
  response: CalendarBoardProjectionResponse,
  slotMinutes = 5,
): CalendarWindowProjection => {
  const events = response.events ?? []
  const contactOrders = dedupeById(response.contact_orders ?? [])

  return {
    events,
    externalBusyIntervals: response.external_busy_intervals ?? [],
    routeTransitions: response.route_transitions ?? [],
    workOrders: dedupeById(response.work_orders ?? []),
    properties: dedupeById(response.properties ?? []),
    customers: dedupeById(response.customers ?? []),
    contactPoints: dedupeById(response.contact_points ?? []),
    contactOrderByEventId: buildContactOrderByEventId({ events, contactOrders }),
    planningSettings: normalizeCalendarPlanningSettings(
      response.planning_settings as Parameters<typeof normalizeCalendarPlanningSettings>[0],
      slotMinutes,
    ),
  }
}
